/**
 * <Wordmark /> — Symplee brand mark, rendered in real typography.
 *
 * Replaces the AI-generated raster wordmark PNGs. Uses **Geist Sans Variable**
 * (self-hosted via /public/fonts/Geist-Variable.woff2; @font-face declared in
 * index.css). Geist's geometric proportions render the "symplee" mark at the
 * same crispness that Linear, Vercel and Anthropic use for their wordmarks —
 * no raster blur, no transparent-padding sizing surprises.
 *
 * Props
 * -----
 *   variant: "light" (default) | "dark" | "gradient"
 *     light    → solid slate-900 (#0F172A); for white / near-white surfaces.
 *     dark     → solid white; for dark surfaces and glass.
 *     gradient → indigo→teal text-clip; reserve for ONE hero moment per page.
 *
 *   size:    "sm" | "md" (default) | "lg" | "xl"
 *     sm  → text-lg   (nav-strip / footer line)
 *     md  → text-2xl  (top nav / sidebar)
 *     lg  → text-4xl  (auth pages)
 *     xl  → text-6xl  (landing hero)
 *
 * Accessibility
 * -------------
 *   We wrap the literal lowercase "symplee" in <span role="img" aria-label="Symplee">
 *   so screen readers announce the brand correctly, not "s-y-m-p-l-e-e".
 *
 * Graceful fallback
 * -----------------
 *   If Geist fails to load (network blip, FOUT) the font-family chain falls
 *   back to Inter → system-ui. Letterforms shift slightly; the wordmark still
 *   renders fully.
 */

const VARIANT_CLASSES = {
  light: "text-slate-900",
  dark: "text-white",
  gradient: "brand-text",   // background-clip:text gradient defined in index.css
};

const SIZE_CLASSES = {
  sm: "text-lg",
  md: "text-2xl",
  lg: "text-4xl",
  xl: "text-6xl",
};

export function Wordmark({ variant = "light", size = "md", className = "" }) {
  const tone = VARIANT_CLASSES[variant] || VARIANT_CLASSES.light;
  const scale = SIZE_CLASSES[size] || SIZE_CLASSES.md;
  return (
    <span
      role="img"
      aria-label="Symplee"
      data-testid="symplee-wordmark"
      data-variant={variant}
      data-size={size}
      className={`font-symplee font-semibold leading-none tracking-tight ${scale} ${tone} ${className}`}
    >
      symplee
    </span>
  );
}
